import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users,
  Search,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader2,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Filter
} from 'lucide-react'
import { adminApi, AdminUser, CreateUserRequest, UpdateUserRequest } from '@/api/admin'
import type { UserType } from '@/types/api'

const USER_TYPES: UserType[] = ['individual', 'parent', 'student', 'employee', 'teacher', 'admin']

const USER_TYPE_LABELS: Record<UserType, string> = {
  individual: '개인',
  parent: '학부모',
  student: '학생',
  employee: '직원',
  teacher: '선생님',
  admin: '관리자'
}

interface UserModalProps {
  user?: AdminUser | null
  onClose: () => void
  onSave: (data: CreateUserRequest | UpdateUserRequest) => void
  isLoading: boolean
}

function UserModal({ user, onClose, onSave, isLoading }: UserModalProps) {
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: user?.email || '',
    password: '',
    userType: user?.userType || 'individual',
    fullName: user?.fullName || '',
    displayName: user?.displayName || '',
    phoneNumber: user?.phoneNumber || '',
    isActive: user?.isActive ?? true,
    isEmailVerified: user?.isEmailVerified ?? false
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (user) {
      // Update - don't send password if empty
      const updateData: UpdateUserRequest = { ...formData }
      delete (updateData as any).password
      onSave(updateData)
    } else {
      onSave(formData)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">
            {user ? '사용자 수정' : '새 사용자 생성'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="label">이메일 *</label>
            <input
              type="email"
              className="input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          {!user && (
            <div>
              <label className="label">비밀번호</label>
              <input
                type="password"
                className="input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="소셜 로그인 사용자는 비워두세요"
              />
            </div>
          )}

          <div>
            <label className="label">사용자 유형 *</label>
            <select
              className="input"
              value={formData.userType}
              onChange={(e) => setFormData({ ...formData, userType: e.target.value as UserType })}
              required
            >
              {USER_TYPES.map((type) => (
                <option key={type} value={type}>{USER_TYPE_LABELS[type]}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">이름 *</label>
            <input
              type="text"
              className="input"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">표시 이름</label>
            <input
              type="text"
              className="input"
              value={formData.displayName || ''}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            />
          </div>

          <div>
            <label className="label">전화번호</label>
            <input
              type="tel"
              className="input"
              value={formData.phoneNumber || ''}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 rounded-sm border-slate-300"
              />
              <span className="text-sm text-slate-700">활성 상태</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isEmailVerified}
                onChange={(e) => setFormData({ ...formData, isEmailVerified: e.target.checked })}
                className="w-4 h-4 rounded-sm border-slate-300"
              />
              <span className="text-sm text-slate-700">이메일 인증됨</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary btn-md flex-1">
              취소
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary btn-md flex-1">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : user ? '수정' : '생성'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function UserManagement() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [filterType, setFilterType] = useState<UserType | ''>('')
  const [filterActive, setFilterActive] = useState<boolean | ''>('')
  const [modalUser, setModalUser] = useState<AdminUser | null | undefined>(undefined)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users', { page, keyword, filterType, filterActive }],
    queryFn: () => adminApi.getUsers({
      page,
      size: 20,
      keyword: keyword || undefined,
      userType: filterType || undefined,
      isActive: filterActive === '' ? undefined : filterActive
    }),
    staleTime: 30 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: adminApi.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      setModalUser(undefined)
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) =>
      adminApi.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      setModalUser(undefined)
    }
  })

  const toggleMutation = useMutation({
    mutationFn: adminApi.toggleUserStatus,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
  })

  const deleteMutation = useMutation({
    mutationFn: adminApi.deleteUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(0)
  }

  const handleSave = (formData: CreateUserRequest | UpdateUserRequest) => {
    if (modalUser) {
      updateMutation.mutate({ id: modalUser.id, data: formData as UpdateUserRequest })
    } else {
      createMutation.mutate(formData as CreateUserRequest)
    }
  }

  const handleDelete = (user: AdminUser) => {
    if (confirm(`정말 "${user.email}" 사용자를 삭제하시겠습니까?`)) {
      deleteMutation.mutate(user.id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">사용자 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" />
            사용자 관리
          </h1>
          <p className="text-slate-600 mt-1">총 {data?.totalElements ?? 0}명의 사용자</p>
        </div>
        <button
          onClick={() => setModalUser(null)}
          className="btn-primary btn-md inline-flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          새 사용자
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="이메일, 이름 검색..."
                className="input pl-10"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
          </div>
          <select
            className="input w-40"
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value as UserType | ''); setPage(0) }}
          >
            <option value="">모든 유형</option>
            {USER_TYPES.map((type) => (
              <option key={type} value={type}>{USER_TYPE_LABELS[type]}</option>
            ))}
          </select>
          <select
            className="input w-32"
            value={filterActive === '' ? '' : filterActive.toString()}
            onChange={(e) => { 
              setFilterActive(e.target.value === '' ? '' : e.target.value === 'true')
              setPage(0)
            }}
          >
            <option value="">모든 상태</option>
            <option value="true">활성</option>
            <option value="false">비활성</option>
          </select>
          <button type="submit" className="btn-secondary btn-md">
            <Filter className="w-4 h-4" />
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">ID</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">이메일</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">이름</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">유형</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">상태</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">가입일</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-600">{user.id}</td>
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">{user.email}</td>
                  <td className="px-6 py-4 text-sm text-slate-700">{user.fullName}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium
                      ${user.userType === 'admin' ? 'bg-red-100 text-red-700' :
                        user.userType === 'teacher' ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'}`}>
                      {USER_TYPE_LABELS[user.userType]}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium
                      ${user.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {user.isActive ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {new Date(user.createdAt).toLocaleDateString('ko-KR')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => toggleMutation.mutate(user.id)}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                        title={user.isActive ? '비활성화' : '활성화'}
                      >
                        {user.isActive ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => setModalUser(user)}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                        title="수정"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                        title="삭제"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalElements)} / {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={data.first}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={data.last}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalUser !== undefined && (
        <UserModal
          user={modalUser}
          onClose={() => setModalUser(undefined)}
          onSave={handleSave}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  )
}

